import { Timestamp } from '@angular/fire/firestore';
import { FeatureFlag } from './feature-flag.model';

/** Billing cycle of a price option. */
export type BillingCycle = 'monthly' | 'yearly' | 'custom';

/**
 * A single price option a package can be sold at.
 *   - monthly → months = 1
 *   - yearly  → months = 12
 *   - custom  → any duration (e.g. 3 or 6 months)
 * `price` is the TOTAL charged for `months` months.
 */
export interface PackagePrice {
   cycle: BillingCycle;
   /** Paid billing duration in months — the basis for price & savings. */
   months: number;
   /**
    * Extra free months granted on top of `months` (e.g. "pay 12, get 2 free").
    * Adds to the entitlement duration but NOT to the price or the savings calc,
    * so it shows as a "+N months free" bonus rather than a discount.
    */
   bonusMonths?: number;
   /** Total price (USD) for the whole duration */
   price: number;
   /** Display label, e.g. "Monthly", "Yearly", "6 Months" */
   label: string;
   labelKH?: string;
   /** Highlight this option as the recommended one */
   popular?: boolean;
}

/** Usage caps a package imposes. An absent cap means unlimited. */
export interface PackageLimits {
   /**
    * Max employees a store can keep in HR. Counts active / on-leave staff only
    * (not resigned, terminated or PR profiles) — see
    * `countsTowardEmployeeLimit` in the HR employee model.
    */
   maxEmployees?: number;
}

/**
 * A subscription Package (a.k.a. plan / tier) sold to stores.
 * Stored in Firestore collection:  packages/{packageId}
 *
 * A package bundles:
 *   - one or more price options (monthly / yearly / custom durations)
 *   - a set of feature keys (which {@link FeatureFlag}s it unlocks)
 *
 * A store points at a package via `store.packageId`, and its available
 * features are derived live from that package — see
 * {@link isFeatureEnabledForPackage}.
 */
export interface Package {
   /** Firestore document id */
   id: string;
   /** Human-readable name, e.g. "Basic", "Standard", "Premium" */
   name: string;
   nameKH: string;
   /** Short description shown to admins / customers */
   description: string;
   descriptionKH: string;
   /** Price options this package can be purchased at (at least one) */
   prices: PackagePrice[];
   /** Feature keys unlocked by this package (see FeatureFlag.key) */
   featureKeys: string[];
   /** Usage caps (e.g. employee count). Absent → no caps. */
   limits?: PackageLimits;
   /** Accent colour for badges/cards (hex) */
   color: string;
   /** Material icon name */
   icon: string;
   /** Ordering in lists (ascending) */
   sortOrder: number;
   /** Highlighted as the recommended package */
   popular: boolean;
   /** Inactive packages are hidden from selection but keep existing stores working */
   isActive: boolean;

   // ── Audit ─────────────────────────────────────────────────────────────────
   createdAt: Timestamp;
   updatedAt: Timestamp;
   updatedBy: { uid: string; displayName: string };
}

/** Fields set by the admin form (audit + id are filled by the service) */
export type PackageDraft = Omit<
   Package,
   'id' | 'createdAt' | 'updatedAt' | 'updatedBy'
>;

/**
 * Resolve whether a feature is available for a store, taking its assigned
 * package into account.
 *
 * Precedence (highest first):
 *   1. Per-store BLOCK override  → always hidden
 *   2. Per-store ALLOW override  → always visible (bypasses package + global)
 *   3. No package assigned        → legacy behaviour (global toggle only)
 *   4. Package assigned           → visible only if globally enabled AND the
 *                                   feature is included in the package
 *
 * Keeping the manual override lists on top means admins can still grant or
 * revoke individual features per store regardless of the package.
 */
export function isFeatureEnabledForPackage(
   flag: FeatureFlag,
   storeId: string,
   pkg: Package | null | undefined,
): boolean {
   if (flag.blockedStoreIds?.includes(storeId)) return false;
   if (flag.allowedStoreIds?.includes(storeId)) return true;
   // No package → preserve the original global-only behaviour (backward compat).
   if (!pkg) return flag.globalEnabled;
   // Package-gated: must be globally on AND part of the package's feature set.
   return flag.globalEnabled && (pkg.featureKeys?.includes(flag.key) ?? false);
}

/**
 * The package's employee cap, or null when unlimited (no package, no cap, or
 * a non-positive value).
 */
export function maxEmployeesOf(pkg: Package | null | undefined): number | null {
   const max = pkg?.limits?.maxEmployees;
   return typeof max === 'number' && max > 0 ? Math.floor(max) : null;
}

// ── Pricing helpers ──────────────────────────────────────────────────────────

/** Default label for a cycle/duration. */
export function defaultPriceLabel(cycle: BillingCycle, months: number): string {
   if (cycle === 'monthly') return 'Monthly';
   if (cycle === 'yearly') return 'Yearly';
   return months === 1 ? '1 Month' : `${months} Months`;
}

/** Effective price per month for a price option (for comparison / "savings"). */
export function pricePerMonth(price: PackagePrice): number {
   return price.months > 0 ? price.price / price.months : price.price;
}

/**
 * Total entitlement duration in months = paid months + bonus (free) months.
 * Use this for extending a subscription (checkout / approval), NOT `months`.
 */
export function totalMonths(price: PackagePrice): number {
   return price.months + (price.bonusMonths || 0);
}

/**
 * Effective price per month over the FULL entitlement (paid + free months).
 * With free months this is lower than {@link pricePerMonth}, so it lines up
 * with the savings % shown to the customer.
 */
export function effectivePricePerMonth(price: PackagePrice): number {
   const total = totalMonths(price);
   return total > 0 ? price.price / total : price.price;
}

/**
 * Return a package's price options, transparently upgrading legacy docs that
 * still carry a single `monthlyPrice` field (pre-`prices[]` schema). Old docs
 * are migrated to the new shape the next time they're saved.
 */
export function normalizePrices(
   pkg: (Partial<Package> & { monthlyPrice?: number }) | null | undefined,
): PackagePrice[] {
   if (pkg?.prices && pkg.prices.length > 0) return pkg.prices;
   const legacy = pkg?.monthlyPrice;
   if (typeof legacy === 'number') return [monthlyPrice(legacy)];
   return [];
}

/** Prices sorted by duration ascending (monthly first, custom by months). */
export function sortedPrices(pkg: Package | null | undefined): PackagePrice[] {
   return [...normalizePrices(pkg)].sort((a, b) => a.months - b.months);
}

/**
 * The cheapest option by EFFECTIVE per-month (price ÷ paid+free months) —
 * used for a "from $X/mo" headline. Using the effective rate means a yearly
 * option with free months correctly wins over a same-sticker monthly one.
 */
export function fromPrice(
   pkg: Package | null | undefined,
): PackagePrice | null {
   const prices = normalizePrices(pkg);
   if (prices.length === 0) return null;
   return prices.reduce((best, p) =>
      effectivePricePerMonth(p) < effectivePricePerMonth(best) ? p : best,
   );
}

/** The monthly option's price, if the package offers one. */
export function monthlyPriceOf(pkg: Package | null | undefined): number | null {
   const monthly = normalizePrices(pkg).find((p) => p.cycle === 'monthly');
   return monthly ? monthly.price : null;
}

/**
 * The price option matching a billing cycle. For 'monthly'/'yearly' it matches
 * by cycle; if none is tagged, it falls back to the option whose duration fits
 * (1 month / 12 months). Returns null when the package offers no such option.
 */
export function priceForCycle(
   pkg: Package | null | undefined,
   cycle: BillingCycle,
): PackagePrice | null {
   const prices = normalizePrices(pkg);
   const byCycle = prices.find((p) => p.cycle === cycle);
   if (byCycle) return byCycle;
   const months = cycle === 'yearly' ? 12 : cycle === 'monthly' ? 1 : -1;
   return prices.find((p) => p.months === months) ?? null;
}

/**
 * Savings (%) of a price option vs. the package's monthly rate, rounded.
 *
 * Compares the price against paying month-by-month for the FULL entitlement —
 * paid months **plus** free/bonus months — so both a price discount and free
 * months (or a combination) are reflected in the same "save %". A full-price
 * yearly with 2 free months therefore still shows a saving.
 *
 * Returns 0 when there's no monthly option or no saving.
 */
export function savingsPercent(
   pkg: Package | null | undefined,
   price: PackagePrice,
): number {
   const monthly = monthlyPriceOf(pkg);
   if (!monthly || monthly <= 0) return 0;
   const fullPrice = monthly * totalMonths(price);
   if (fullPrice <= 0) return 0;
   const pct = Math.round((1 - price.price / fullPrice) * 100);
   return pct > 0 ? pct : 0;
}

/** Convenience builders for the standard cycles. */
export function monthlyPrice(price: number): PackagePrice {
   return { cycle: 'monthly', months: 1, price, label: 'Monthly' };
}
export function yearlyPrice(price: number): PackagePrice {
   return { cycle: 'yearly', months: 12, price, label: 'Yearly' };
}

/** Seed packages created on first load of the admin Packages page. */
export const DEFAULT_PACKAGES: PackageDraft[] = [
   {
      name: 'Basic',
      nameKH: 'មូលដ្ឋាន',
      description: 'Core point-of-sale for a single small shop.',
      descriptionKH: 'ប្រព័ន្ធលក់ជាមូលដ្ឋានសម្រាប់ហាងតូច។',
      prices: [monthlyPrice(8.5), yearlyPrice(85)],
      featureKeys: [],
      color: '#2579a6',
      icon: 'inventory_2',
      sortOrder: 1,
      popular: false,
      isActive: true,
   },
   {
      name: 'Standard',
      nameKH: 'ស្តង់ដារ',
      description: 'Everything in Basic plus loyalty, PR and knowledge hub.',
      descriptionKH: 'រួមបញ្ចូលមូលដ្ឋាន បូកភក្ដីភាព PR និងមូលដ្ឋានចំណេះដឹង។',
      prices: [monthlyPrice(15), yearlyPrice(180)],
      featureKeys: [
         'reservations',
         'customer_display',
         'kot_display',
         'material_stock',
         'approval_requests',
         'loyalty',
         'pr_management',
         'knowledge_hub',
         'delivery',
      ],
      color: '#7c3aed',
      icon: 'workspace_premium',
      sortOrder: 2,
      popular: true,
      isActive: true,
   },
   {
      name: 'Premium',
      nameKH: 'ព្រីមៀម',
      description: 'Full suite: HR, payroll and accounting included.',
      descriptionKH: 'ពេញលេញ៖ ធនធានមនុស្ស ប្រាក់ខែ និងគណនេយ្យ។',
      prices: [monthlyPrice(25), yearlyPrice(300)],
      featureKeys: [
         'reservations',
         'customer_display',
         'kot_display',
         'material_stock',
         'approval_requests',
         'loyalty',
         'pr_management',
         'knowledge_hub',
         'delivery',
         'hr',
         'accounting',
      ],
      color: '#d97706',
      icon: 'diamond',
      sortOrder: 3,
      popular: false,
      isActive: true,
   },
];
